<template>
    <Overlay :show="loading">
        <div>
            <b-card title="Supplier Reply Review">
                <b-alert variant="warning" show>
                    <div class="alert-body p-1">
                        This internal tool endpoint is disabled in production.
                    </div>
                </b-alert>

                <b-alert
                    v-if="errorMessage"
                    variant="danger"
                    show
                >
                    <div class="alert-body p-1">
                        {{ errorMessage }}
                    </div>
                </b-alert>

                <b-form @submit.prevent="submit">
                    <b-form-group label="Supplier">
                        <SupplierSearch
                            :show-selected-badge="false"
                            placeholder="Search supplier"
                            @selected="onSupplierSelected"
                        />
                    </b-form-group>

                    <b-alert
                        v-if="selectedSupplier"
                        variant="secondary"
                        show
                    >
                        <div class="alert-body p-1">
                            <div>
                                <strong>Selected supplier:</strong>
                                {{ selectedSupplier.title }}
                            </div>
                            <div>
                                <strong>ID:</strong>
                                {{ selectedSupplier.id }}
                                <span v-if="selectedSupplier.vatNumber">
                                    <strong class="ml-1">VAT:</strong>
                                    {{ selectedSupplier.vatNumber }}
                                </span>
                            </div>
                        </div>
                    </b-alert>

                    <b-form-group label="PDF documents">
                        <b-form-file
                            v-model="documents"
                            multiple
                            accept="application/pdf,application/x-pdf"
                            placeholder="Choose PDF files or drop them here"
                            drop-placeholder="Drop PDF files here"
                        />
                    </b-form-group>

                    <b-button
                        type="submit"
                        variant="primary"
                        :disabled="loading"
                    >
                        <b-spinner
                            v-if="loading"
                            small
                            class="mr-50"
                        />
                        Run review
                    </b-button>
                </b-form>
            </b-card>

            <b-card
                v-if="result"
                title="Result"
            >
                <table class="table table-borderless mb-2">
                    <tbody>
                    <tr>
                        <th class="pl-0">Message ID</th>
                        <td>{{ result.messageId }}</td>
                    </tr>
                    <tr>
                        <th class="pl-0">Supplier ID</th>
                        <td>{{ result.supplierId }}</td>
                    </tr>
                    <tr>
                        <th class="pl-0">Received At</th>
                        <td>{{ result.receivedAt }}</td>
                    </tr>
                    </tbody>
                </table>

                <b-table
                    :items="result.documents"
                    :fields="documentFields"
                    responsive
                    bordered
                    striped
                />
            </b-card>
        </div>
    </Overlay>
</template>

<script>
import {
    BAlert,
    BButton,
    BCard,
    BForm,
    BFormFile,
    BFormGroup,
    BSpinner,
    BTable,
} from 'bootstrap-vue'
import { SupplierSearch } from '@core/components/supply-chain'
import { InternalToolsApi } from '@core/api'
import Overlay from '@core/components/common/Overlay.vue'

export default {
    components: {
        BAlert,
        BButton,
        BCard,
        BForm,
        BFormFile,
        BFormGroup,
        BSpinner,
        BTable,
        Overlay,
        SupplierSearch,
    },
    data() {
        return {
            selectedSupplier: null,
            documents: [],
            loading: false,
            errorMessage: '',
            result: null,
            documentFields: [
                { key: 'filename', label: 'Filename' },
                { key: 'fileReferenceId', label: 'File Reference ID' },
            ],
        }
    },
    methods: {
        onSupplierSelected(supplier) {
            this.selectedSupplier = supplier
            this.errorMessage = ''
        },
        validate() {
            if (!this.selectedSupplier) {
                this.errorMessage = 'Select a supplier.'
                return false
            }

            if (!this.documents || this.documents.length === 0) {
                this.errorMessage = 'Choose at least one PDF document.'
                return false
            }

            return true
        },
        submit() {
            if (!this.validate()) {
                return
            }

            this.loading = true
            this.errorMessage = ''
            this.result = null

            InternalToolsApi.product.supplierReplyReview
                .run(this.selectedSupplier.id, this.documents)
                .then(response => {
                    this.result = response.data
                })
                .catch(error => {
                    this.errorMessage = this.getErrorMessage(error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        getErrorMessage(error) {
            const status = error && error.response ? error.response.status : null
            const data = error && error.response ? error.response.data : null

            if (status === 403) {
                return 'You are not allowed to run this internal tool.'
            }

            if (status === 404) {
                return 'This endpoint is disabled in production or is not available.'
            }

            if (status === 422) {
                return this.getValidationErrorMessage(data)
            }

            return 'The review could not be started.'
        },
        getValidationErrorMessage(data) {
            if (!data) {
                return 'Validation failed. Check supplier and PDF documents.'
            }

            if (typeof data.message === 'string') {
                return data.message
            }

            if (data.errors && typeof data.errors === 'object') {
                return Object.keys(data.errors)
                    .map(key => data.errors[key])
                    .reduce((messages, value) => messages.concat(value), [])
                    .join(' ')
            }

            return 'Validation failed. Check supplier and PDF documents.'
        },
    },
}
</script>
